import { FC, ReactElement, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import i18next from 'i18next';
import { ExerciseTagDto } from '../../../../../../shared/dtos/exerciseTag.dto';
import { ExerciseFilterDto } from '../../../../../../shared/dtos/exerciseFilter.dto';
import ExercisePickerChip from './ExercisePickerChip';

type ExercisePickerChipsProps = {
  filters?: ExerciseFilterDto;
  selectedByCategory: Record<string, string[]>;
  searchKey: string;
  onRemoveTag: (tag: ExerciseTagDto) => void;
  onClearSearch: () => void;
  onResetAll: () => void;
};

const ExercisePickerChips: FC<ExercisePickerChipsProps> = ({
  filters,
  selectedByCategory,
  searchKey,
  onRemoveTag,
  onClearSearch,
  onResetAll,
}: ExercisePickerChipsProps): ReactElement | null => {
  const userLanguage: string = i18next.language;

  const selectedTags: ExerciseTagDto[] = useMemo(() => {
    const tagsByCategory: Record<string, ExerciseTagDto[]> = filters?.tags ?? {};
    const allTags: ExerciseTagDto[] = Object.values(tagsByCategory).flat();
    const selectedIds: Set<string> = new Set(Object.values(selectedByCategory).flat());

    return allTags.filter((tag: ExerciseTagDto) => selectedIds.has(tag.id));
  }, [filters, selectedByCategory]);

  if (selectedTags.length === 0 && searchKey.length === 0) {
    return null;
  }

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {searchKey.length > 0 && (
        <ExercisePickerChip
          label={i18next.t('exerciseModal.searchChip', { searchKey })}
          removeLabel={i18next.t('generic.clearSearch')}
          onRemove={onClearSearch}
        />
      )}

      {selectedTags.map((tag: ExerciseTagDto) => (
        <ExercisePickerChip
          key={`chip-${tag.id}`}
          label={userLanguage === 'en' ? tag.en : tag.de}
          removeLabel={i18next.t('exerciseModal.removeFilter')}
          onRemove={() => onRemoveTag(tag)}
        />
      ))}

      <Button
        variant="link"
        size="sm"
        className="p-0 ms-1 text-decoration-none"
        style={{ fontSize: '1.3rem', color: '#a0a3b1' }}
        onClick={onResetAll}
      >
        {i18next.t('generic.resetFilter')}
      </Button>
    </div>
  );
};

export default ExercisePickerChips;
